/**
 * @license
 * Copyright Akveo. All Rights Reserved.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 */
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { APP_INITIALIZER, NgModule } from "@angular/core";
import { HttpClient, HTTP_INTERCEPTORS, HttpClientModule, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { CoreModule } from "./@core/core.module";
import { ThemeModule } from "./@theme/theme.module";
import { AppComponent } from "./app.component";
import { AppRoutingModule } from "./app-routing.module";
import {
  NbDatepickerModule,
  NbMenuModule,
  NbSidebarModule,
  NbToastrModule,
  NbTimepickerModule,
  NbDialogModule,
} from "@nebular/theme";
import {
  NbAuthModule,
  NbAuthService,
} from "@nebular/auth";
import { environment, LanguageSettings } from "../environments/environment";
import { NgSelectModule } from "@ng-select/ng-select";
import { FormsModule } from "@angular/forms";
import { TranslateModule, TranslateLoader } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { AuthService } from "./auth.service";
import { AuthInterceptor } from "./auth.interceptor";
// import { OwlDateTimeModule, OwlNativeDateTimeModule } from "ng-pick-datetime";
import { GoogleMapsModule } from "@angular/google-maps";
// import { FormsModule } from '@angular/forms';
import { AuthModule } from "./auth/auth.module";
import { DataService } from "./admin/data.service";
import { CustomTranslateLoader } from "./translations/translation.httploader";
import { NgxEchartsModule } from "ngx-echarts";
import { InterceptorService } from "./interceptor.service";
import { EnvConfigService } from './env-config.service';
import { firstValueFrom } from 'rxjs';
import { ApiInterceptor } from './api.interceptor'
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFireMessagingModule } from '@angular/fire/compat/messaging';

// Env Configuration
const envConfig = (config: EnvConfigService) => {
  return () => {
    return firstValueFrom(config.loadConfig());
  }
}

// Translation
export function createTranslateLoader(httpClient: HttpClient) {
  const checkLang = LanguageSettings["fetchTranslateFilesFromAPI"]
    ? LanguageSettings["fetchTranslateFilesFromAPI"]
    : false;
  if (checkLang) {
    return new CustomTranslateLoader(httpClient, `${environment.BASEURL}` + "public/locale", '.json');
  } else {
    return new TranslateHttpLoader(httpClient, "./assets/i18n/", ".json");
  }
}

export function initializeApp(interceptorService: InterceptorService) {
  return () => interceptorService.initializeApp();
}

@NgModule({
  declarations: [AppComponent],
  exports: [
    BrowserModule,
    FormsModule,
    HttpClientModule,
    TranslateModule
  ],
  bootstrap: [AppComponent],
  imports: [
    FormsModule,
    AuthModule,
    NgSelectModule,
    BrowserModule,
    NbDialogModule.forRoot(),
    AngularFireModule.initializeApp(environment.firebaseConfig),
    AngularFireMessagingModule,
    NbToastrModule.forRoot(),
    BrowserAnimationsModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: (createTranslateLoader),
        deps: [HttpClient]
      }
    }),
    AppRoutingModule,
    NbSidebarModule.forRoot(),
    NbMenuModule.forRoot(),
    ThemeModule.forRoot(),
    NbAuthModule.forRoot({
      // strategies: [
      //   NbPasswordAuthStrategy.setup({
      //     name: "email",
      //     baseEndpoint: "http://localhost:3001/adminapi/",
      //     login: {
      //       endpoint: "login",
      //       method: "post",
      //       redirect: {
      //         success: "/admin/dashboard",
      //         failure: null,
      //       },
      //       defaultErrors: [
      //         "Login/Email combination is not correct, please try again.",
      //       ],
      //       defaultMessages: ["You have been successfully logged in."],
      //     },
      //     register: {
      //       endpoint: "sign-up",
      //       method: "post",
      //     },
      //     logout: {
      //       endpoint: "logout",
      //       method: "get",
      //       redirect: {
      //         success: "/auth/login",
      //         failure: null,
      //       },
      //     },
      //     requestPass: {
      //       endpoint: "forget-password",
      //       method: "put",
      //       redirect: {
      //         success: "/auth/forget-password",
      //         failure: null,
      //       },
      //     },
      //     resetPass: {
      //       endpoint: "/auth/reset-pass",
      //       method: "post",
      //     },
      //     token: {
      //       class: NbAuthJWTToken,
      //       key: "data.token", // this parameter tells where to look for the token
      //     },
      //   }),
      // ],
      // forms: {
      //   login: {
      //     rememberMe: false,
      //     strategy: "email",
      //     redirectDelay: 2000,
      //     showMessages: {
      //       success: true,
      //       error: true,
      //     },
      //   },
      // },
    }),
    NbDatepickerModule.forRoot(),
    NbTimepickerModule.forRoot(),
    CoreModule.forRoot(),
    NgxEchartsModule.forRoot({
      // optional configuration for echarts
      echarts: () => import('echarts'), // or import('echarts/core') for tree-shaking
    }),
    // OwlDateTimeModule,
    // OwlNativeDateTimeModule,
    GoogleMapsModule
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [InterceptorService],  // Make sure InterceptorService is in the dependencies here
      multi: true,
    },
    { provide: HTTP_INTERCEPTORS, useClass: ApiInterceptor, multi: true },
    InterceptorService,
    AuthService,
    EnvConfigService,
    {
      provide: APP_INITIALIZER,
      useFactory: envConfig,
      multi: true,
      deps: [EnvConfigService],
    },
    DataService,
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    NbAuthService,
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimationsAsync(),
  ]
})
export class AppModule {

}
